/**
 * Lo que incluye cada plan, escrito una sola vez.
 *
 * Lo usan la comparativa de planes y el resumen de lo que se pierde al
 * cancelar. Antes vivía suelto en la pantalla de planes, así que el aviso de
 * cancelación tenía que reescribir la misma lista a mano — y una lista de
 * ventajas escrita dos veces acaba prometiendo en un sitio algo que el otro ya
 * no dice.
 *
 * Los topes salen de `SITE_PLAN_LIMITS`, que es lo mismo que aplica el
 * servidor: aquí se escribe cómo se leen, nunca cuánto valen.
 *
 * ── Por qué son funciones y no constantes ───────────────────────────────────
 *
 * Porque hay que traducirlas, y una constante de módulo no puede llamar a un
 * hook. Reciben el traductor y devuelven la lista ya escrita. Al pasarlo por
 * parámetro, el compilador señala cada sitio que hay que tocar: si esto fuera
 * una constante que cambia de valor por dentro, la mitad de las pantallas
 * seguiría enseñando español sin que nada lo dijera.
 */
import type { Translate } from '@webbuilder/i18n'
import { formatPlanLimit, PROJECT_LIMITS, SITE_PLAN_LIMITS, type LimitedResource } from './planLimits'

/** Una ventaja. `annualOnly` la marca como exclusiva del plan anual. */
export type PlanFeature = { label: string; annualOnly?: boolean }

export function formatContactLimit(limit: number | null, t: Translate): string {
  return limit === null
    ? t('plans.feat.contactsUnlimited')
    : t('plans.feat.contacts', { count: formatPlanLimit(limit) })
}

/**
 * Los cupos de las apps de negocio en Business.
 *
 * Textos y no números: cada uno lleva su unidad —«/mes», «servicios»— y sin
 * ella la tabla queda llena de cifras sueltas que no dicen de qué son.
 */
/**
 * Cuántos cursos anuncia cada plan. Es texto de venta: los cursos todavía no
 * salen (la beta no los trae), así que ningún servidor aplica estos números.
 * El tope de verdad de un curso es el almacenamiento, porque sus videos van en
 * MP4 a R2 y cuentan contra los GB del plan.
 */
export const COURSE_LIMITS = { pro: 1, business: 5, creator: 25 } as const

/**
 * «5 · Próximamente»: el cupo y que todavía no está. Los cursos los suben y los
 * venden los clientes —véltica no los hace—, y hasta que salgan no se puede
 * prometer el número sin decir que aún no existe.
 */
export function courseLimitLabel(count: number, t: Translate): string {
  return `${count} · ${t('plans.limit.soon')}`
}

export function businessAppLimits(t: Translate) {
  return {
    formSubmissions: t('plans.limit.submissions', { count: formatPlanLimit(5_000) }),
    /* La bandeja y el marketing por correo aún no existen: se anuncian como
       «Próximamente» y no con un cupo, que prometería algo que no se puede usar. */
    marketingEmails: t('plans.limit.soon'),
    /* Del mismo sitio que lo aplica el servidor, no de un número escrito aquí:
       decía 20 ofertas cuando el tope de Business es otro, y una tabla de
       precios que promete menos de lo que da es tan mala como la que promete
       de más — sólo que se descubre más tarde.

       La agenda ya no tiene cupo propio: sus servicios gastan de este mismo
       número, así que su fila dice que está incluida y no cuántos caben. */
    offers: SITE_PLAN_LIMITS.business.offers === null
      ? t('plans.limit.unlimitedF')
      : t('plans.limit.offers', { count: formatPlanLimit(SITE_PLAN_LIMITS.business.offers) }),
    teamMembers: SITE_PLAN_LIMITS.business.teamMembers === null
      ? t('plans.limit.unlimitedM')
      : formatPlanLimit(SITE_PLAN_LIMITS.business.teamMembers),
    courses: courseLimitLabel(COURSE_LIMITS.business, t),
  } as const
}

/** «2 proyectos» / «1 proyecto», que es lo primero que se elige de un plan. */
function formatProjectLimit(limit: number | null, t: Translate): string {
  if (limit === null) return t('plans.feat.projectsUnlimited')
  const count = formatPlanLimit(limit)
  return limit === 1 ? t('plans.feat.projectOne', { count }) : t('plans.feat.projects', { count })
}

const free = SITE_PLAN_LIMITS.free
const pro = SITE_PLAN_LIMITS.pro
const business = SITE_PLAN_LIMITS.business

/**
 * «3 ofertas publicadas», el cupo que más se topa.
 *
 * Faltaba en Starter y en Pro: sólo Business decía cuántas caben, así que el
 * aviso de «has llegado al tope de ofertas» nombraba un límite que no aparecía
 * en ninguna lista, y al ir a comparar planes tampoco estaba. Un tope que sólo
 * se conoce al chocar con él no se puede tener en cuenta al elegir plan.
 *
 * Sale de `SITE_PLAN_LIMITS`, que es lo mismo que aplica el servidor, para que
 * no pueda prometer un número distinto del que se comprueba.
 */
function offersLine(limits: { offers: number | null }, t: Translate): string {
  return limits.offers === null
    ? t('plans.limit.unlimitedF')
    : t('plans.limit.offers', { count: formatPlanLimit(limits.offers) })
}

/** «Blog: 10 entradas publicadas», del mismo tope que aplica el servidor. */
export function blogLine(limits: { blogPosts: number | null }, t: Translate): string {
  return limits.blogPosts === null
    ? t('plans.feat.blogUnlimited')
    : t('plans.feat.blogPosts', { count: formatPlanLimit(limits.blogPosts) })
}

/** «30 colecciones · 1.000 elementos CMS», que siempre van juntos. */
function cmsLine(limits: { collections: number | null; cmsItems: number | null }, t: Translate): string {
  return t('plans.feat.cms', {
    collections: formatPlanLimit(limits.collections),
    items: formatPlanLimit(limits.cmsItems),
  })
}

/**
 * En qué se convierte cada ventaja al bajar al plan gratuito.
 *
 * `to` en `null` es lo que desaparece sin equivalente: el dominio incluido del
 * plan anual no tiene versión gratis, simplemente deja de estar. Distinguirlo de
 * un tope que baja importa — «10.000 contactos → 100 contactos» y «dominio
 * incluido → nada» son dos pérdidas muy distintas, y presentarlas igual hace que
 * la segunda parezca menos grave de lo que es.
 *
 * Aquí sólo va lo que DE VERDAD cambia al bajar de plan. Hubo una fila de «SEO
 * avanzado y redirecciones» que no limitaba nada —el SEO se edita en cualquier
 * plan y las redirecciones no se aplican al publicar— y prometía una pérdida
 * que no ocurría.
 *
 * Las cifras salen de `SITE_PLAN_LIMITS`, las dos columnas: así el «de X a Y»
 * no puede quedarse a medias cuando cambie un plan.
 */
export type PlanDowngrade = {
  from: string
  to: string | null
  annualOnly?: boolean
  /**
   * El tope al que corresponde, cuando corresponde a alguno.
   *
   * Es lo que permite cruzar este renglón con lo que la cuenta tiene de verdad
   * y avisar de que ya lo excede. Sin él habría que emparejar por el texto, que
   * se rompe en cuanto alguien reescribe «30 páginas».
   */
  resource?: LimitedResource
}

export function planDowngrade(t: Translate): Record<'pro' | 'business', PlanDowngrade[]> {
  const apps = businessAppLimits(t)
  const freeCms = cmsLine(free, t)
  /** Lo que el plan gratuito pone en lugar de lo que se pierde, cuando pone algo. */
  const common: PlanDowngrade[] = [
    { from: t('plans.feat.customDomain'), to: t('plans.feat.subdomain') },
    { from: t('plans.feat.noBadge'), to: t('plans.feat.badge') },
    /* El gratuito no tiene analíticas: se pierden, no bajan a una versión menor. */
    { from: t('plans.feat.analyticsFull'), to: null },
  ]
  const freePages = t('plans.feat.pages', { count: formatPlanLimit(free.pages) })
  return {
    pro: [
      { from: t('plans.feat.freeDomain'), to: null, annualOnly: true },
      /* Sin `resource`: el cupo de proyectos se cuenta por CUENTA, y el resumen de
         cancelación cruza estos renglones con lo que tiene cada sitio. Los que ya
         sobran se avisan aparte. */
      { from: formatProjectLimit(PROJECT_LIMITS.pro, t), to: formatProjectLimit(PROJECT_LIMITS.free, t) },
      { from: t('plans.feat.pages', { count: formatPlanLimit(pro.pages) }), to: freePages, resource: 'pages' },
      { from: cmsLine(pro, t), to: freeCms, resource: 'collections' },
      { from: blogLine(pro, t), to: blogLine(free, t), resource: 'blogPosts' },
      { from: formatContactLimit(pro.contacts, t), to: formatContactLimit(free.contacts, t), resource: 'contacts' },
      /* Con `resource`, el resumen de cancelación puede decir cuántas ofertas
         pasan del tope nuevo. Bajar de treinta a tres despublica las que
         sobren, y hasta ahora eso no se avisaba en ninguna parte. */
      { from: offersLine(pro, t), to: offersLine(free, t), resource: 'offers' },
      /* El gratis no invita a nadie. Quien ya está dentro se queda —bajar de
         plan no borra nada, ver `allowsWrite`—, pero no entra nadie más; con
         `resource`, el resumen avisa de cuántos sobran. */
      { from: t('plans.feat.team', { count: formatPlanLimit(pro.teamMembers) }), to: t('plans.feat.team', { count: formatPlanLimit(free.teamMembers) }), resource: 'teamMembers' },
      ...common,
    ],
    business: [
      { from: t('plans.feat.freeDomain'), to: null, annualOnly: true },
      { from: formatProjectLimit(PROJECT_LIMITS.business, t), to: formatProjectLimit(PROJECT_LIMITS.free, t) },
      { from: t('plans.feat.unlimitedPages'), to: freePages, resource: 'pages' },
      { from: cmsLine(business, t), to: freeCms, resource: 'collections' },
      { from: t('plans.feat.contactsUnlimited'), to: formatContactLimit(free.contacts, t), resource: 'contacts' },
      { from: offersLine(business, t), to: offersLine(free, t), resource: 'offers' },
      ...common,
      { from: t('plans.feat.forms', { limit: apps.formSubmissions }), to: null },
      { from: t('plans.feat.sales', { limit: apps.offers }), to: null },
      { from: t('plans.feat.team', { count: apps.teamMembers }), to: null },
      { from: t('plans.feat.supportPriority'), to: null },
    ],
  }
}
